import * as React from 'react';
import styled, { AnyStyledComponent } from 'styled-components';
import AnimatedInput from '../AnimatedInput/AnimatedInput';
import { CreditCard } from '../creditCard/CreditCard';
import { CreditCardCVV } from '../creditCardCVV/creditCardCVV';
import { CreditCardExpiration } from '../creditCardExpiration/creditCardExpiration';
import Input2, { InputBackground } from '../input/Input2';
import InputGroup from '../inputGroup/InputGroup';

// ############################ Styles ###########################
// tslint:disable:variable-name
const SCardSection = styled.section(
  ({ theme }) => `
  margin-bottom: ${theme.spacing.large};
  margin-top: ${theme.spacing.large};
`
);

const SCreditCard = styled(CreditCard as any)(({ theme }) => ({
  width: '100%',
  maxWidth: 500,
  marginBottom: theme.spacing.medium,
}));

const SCardExpirationCvvSection = styled.section(() => ({
  width: '100%',
  maxWidth: 500,
  display: 'flex',
  justifyContent: 'flex-start',
}));

const SCreditCardExpiration = styled(CreditCardExpiration as any)(({ theme }) => ({
  marginRight: theme.spacing.larger,
  flexGrow: 3,
}));

const SCreditCardCVV = styled(CreditCardCVV as any)(() => ({
  flexGrow: 2,
}));
// tslint:enable:variable-name
// ############################ End Styles ###########################

interface ICreditCardFormValues {
  cardNumber: string;
  creditCardExpiration: string;
  cvv: string;
  firstName: string;
  middleName: string;
  lastName: string;
}

interface ICreditCardFormProps {
  onChange: (value: string, name: string) => void;
  hasName?: boolean;
}

// tslint:disable-next-line:variable-name
const CreditCardForm: React.FC<ICreditCardFormProps> = (props) => {
  const expirationMonthRef = React.useRef<HTMLInputElement>(null);
  const expirationYearRef = React.useRef<HTMLInputElement>(null);
  const cvvRef = React.useRef<HTMLInputElement>(null);
  const [values, setValues] = React.useState<ICreditCardFormValues>({
    cardNumber: '',
    creditCardExpiration: '',
    cvv: '',
    firstName: '',
    middleName: '',
    lastName: '',
  });

  const handleOnChange = React.useCallback(
    (value, name, e?: HTMLInputElement) => {
      setValues({ ...values, [name]: value });
      props.onChange(value, name);
    },
    [...Object.values(values)]
  );

  const handleOnChangeEvent = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      handleOnChange(value, name);
    },
    [handleOnChange]
  );

  return (
    <SCardSection>
      <SCreditCard onFilled={handleOnChange} value={values.cardNumber} next={expirationMonthRef} />
      <SCardExpirationCvvSection>
        <SCreditCardExpiration
          onFilled={handleOnChange}
          value={values.creditCardExpiration}
          expirationMonthRef={expirationMonthRef}
          expirationYearRef={expirationYearRef}
          next={cvvRef}
        />
        <SCreditCardCVV onFilled={handleOnChange} value={values.cvv} cvvRef={cvvRef} />
      </SCardExpirationCvvSection>
      {props.hasName && (
        <InputGroup dataAt="customerDetailsInputs1" label="Cardholder Name">
          <Input2
            autoComplete="cc-given-name"
            dataAt="firstName"
            name="firstName"
            background={InputBackground.White}
            className="inputInGroup"
            onChange={handleOnChangeEvent}
            value={values.firstName || ''}
            placeholder="First Name"
          />
          <Input2
            autoComplete="cc-additional-name"
            dataAt="middleName"
            name="middleName"
            background={InputBackground.White}
            className="inputInGroup"
            onChange={handleOnChangeEvent}
            value={values.middleName || ''}
            placeholder="Middle Name"
          />
          <Input2
            autoComplete="cc-family-name"
            dataAt="lastName"
            name="lastName"
            background={InputBackground.White}
            className="inputInGroup"
            onChange={handleOnChangeEvent}
            value={values.lastName || ''}
            placeholder="Last Name"
          />
        </InputGroup>
      )}
    </SCardSection>
  );
};

export default CreditCardForm;
